import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { changePassword, adminMe } from "@/lib/apiClient";

const inputCls =
  "bg-[hsl(220_30%_8%)] border-border focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] pr-10";

function PasswordField({ id, label, value, onChange, error, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          data-testid={testId}
          aria-invalid={!!error}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          tabIndex={-1}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-destructive" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
}

export default function AccountPanel() {
  const [email, setEmail] = useState("");
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminMe().then((d) => setEmail(d.email)).catch(() => {});
  }, []);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.current) e.current = "Enter your current password.";
    if (!form.next) e.next = "Enter a new password.";
    else if (form.next.length < 8) e.next = "New password must be at least 8 characters.";
    if (form.confirm !== form.next) e.confirm = "Passwords do not match.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await changePassword(form.current, form.next);
      toast.success("Password updated. Use it next time you sign in.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to update password.";
      toast.error(msg);
      if (/current password/i.test(msg)) setErrors((e) => ({ ...e, current: msg }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Account</h2>
        <p className="mt-1 text-sm text-muted-foreground">Manage your admin sign-in credentials.</p>
      </div>

      <div className="mb-5 flex items-center gap-3 rounded-[var(--radius-card)] border border-border bg-card p-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-secondary">
          <ShieldCheck className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Signed in as</p>
          <p className="font-medium" data-testid="account-email">{email || "—"}</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5 rounded-[var(--radius-card)] border border-border bg-card p-6" data-testid="change-password-form">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" />
          <h3 className="text-base font-semibold">Change password</h3>
        </div>
        <PasswordField id="cur" label="Current password" value={form.current} onChange={set("current")} error={errors.current} testId="account-current-password-input" />
        <PasswordField id="new" label="New password" value={form.next} onChange={set("next")} error={errors.next} testId="account-new-password-input" />
        <PasswordField id="conf" label="Confirm new password" value={form.confirm} onChange={set("confirm")} error={errors.confirm} testId="account-confirm-password-input" />
        <p className="text-xs text-muted-foreground">Use at least 8 characters. Choose something strong and unique.</p>
        <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="account-change-password-button">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</> : "Update password"}
        </Button>
      </form>
    </div>
  );
}
